import { useMemo } from 'react';
import { SelectChip } from '@cbhq/cds-web/alpha/select-chip/SelectChip';
import { SearchInput } from '@cbhq/cds-web/controls/SearchInput';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { defineMessages, useIntl } from '@cbhq/i18n';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'activityFilters.searchPlaceholder',
    defaultMessage: 'Search',
    description: 'Placeholder text for the activity audit log search input',
  },
  actor: {
    id: 'activityFilters.actor',
    defaultMessage: 'Actor',
    description: 'Default label for the actor filter chip',
  },
  actionType: {
    id: 'activityFilters.actionType',
    defaultMessage: 'Action type',
    description: 'Default label for the action type filter chip',
  },
  date: {
    id: 'activityFilters.date',
    defaultMessage: 'Date',
    description: 'Default label for the date filter chip',
  },
  anyDate: {
    id: 'activityFilters.anyDate',
    defaultMessage: 'Any date',
    description: 'Option label for clearing the date filter',
  },
  clearAll: {
    id: 'activityFilters.clearAll',
    defaultMessage: 'Clear all',
    description: 'Label for the clear-all button in multi-select filter chips',
  },
  filterByActor: {
    id: 'activityFilters.filterByActor',
    defaultMessage: 'Filter by actor',
    description: 'Accessibility label for the actor filter chip',
  },
  filterByActionType: {
    id: 'activityFilters.filterByActionType',
    defaultMessage: 'Filter by action type',
    description: 'Accessibility label for the action type filter chip',
  },
  filterByDate: {
    id: 'activityFilters.filterByDate',
    defaultMessage: 'Filter by date',
    description: 'Accessibility label for the date filter chip',
  },
});

const selectChipOverrides = {
  classNames: {
    dropdown: 'workspaceSelectHiddenScrollbar',
  },
} as const;

export type ActivityFilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type ActivityFiltersProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  actorFilter: string[];
  onActorFilterChange: (value: string | string[] | null) => void;
  actorOptions: ActivityFilterOption[];
  actionTypeFilter: string[];
  onActionTypeFilterChange: (value: string | string[] | null) => void;
  actionTypeOptions: ActivityFilterOption[];
  dateFilter: string | null;
  onDateFilterChange: (value: string | null) => void;
  dateOptions: ActivityFilterOption[];
};

export function ActivityFilters({
  searchValue,
  onSearchChange,
  actorFilter,
  onActorFilterChange,
  actorOptions,
  actionTypeFilter,
  onActionTypeFilterChange,
  actionTypeOptions,
  dateFilter,
  onDateFilterChange,
  dateOptions,
}: ActivityFiltersProps) {
  const { formatMessage } = useIntl();

  const renderedDateOptions = useMemo(
    () => [{ value: null, label: formatMessage(messages.anyDate) }, ...dateOptions],
    [dateOptions, formatMessage],
  );
  const clearAllLabel = formatMessage(messages.clearAll);

  return (
    <HStack gap={1.5} alignItems="center">
      <Box flexGrow={1} maxWidth={240}>
        <SearchInput
          value={searchValue}
          onChangeText={onSearchChange}
          placeholder={formatMessage(messages.searchPlaceholder)}
          compact
        />
      </Box>

      <SelectChip<'multi', string>
        type="multi"
        {...selectChipOverrides}
        value={actorFilter}
        onChange={onActorFilterChange}
        label={formatMessage(messages.actor)}
        controlAccessibilityLabel={formatMessage(messages.filterByActor)}
        options={actorOptions}
        clearAllLabel={clearAllLabel}
      />

      <SelectChip<'multi', string>
        type="multi"
        {...selectChipOverrides}
        value={actionTypeFilter}
        onChange={onActionTypeFilterChange}
        label={formatMessage(messages.actionType)}
        controlAccessibilityLabel={formatMessage(messages.filterByActionType)}
        options={actionTypeOptions}
        clearAllLabel={clearAllLabel}
      />

      <SelectChip<'single', string>
        type="single"
        {...selectChipOverrides}
        value={dateFilter}
        onChange={onDateFilterChange}
        label={formatMessage(messages.date)}
        controlAccessibilityLabel={formatMessage(messages.filterByDate)}
        options={renderedDateOptions}
      />
    </HStack>
  );
}
